import { useCallback, useState } from 'react';

import { SettlementPendingEntry } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

// ─────────────────────────────────────────────
// useRowSelection
//
// 미정산(업체/상조) 테이블의 집행 대상 배송 선택 상태를 다루는 공용 훅.
// 행 단위(toggleRow)·그룹 단위(toggleGroup) 토글과 초기화(clear)를 제공한다.
// 업체정산·상조정산 두 섹션이 동일 로직을 쓰던 것을 한 곳으로 모은 것.
// ─────────────────────────────────────────────

export interface RowSelection {
  /** 선택된 배송 _id 집합 */
  selected: Set<string>;
  /** 한 건(행) 토글 — 선택돼 있으면 해제, 아니면 추가 */
  toggleRow: (id: string) => void;
  /** 그룹 전체 토글 — 그룹이 모두 선택돼 있으면 전체 해제, 아니면 전체 선택 */
  toggleGroup: (entries: SettlementPendingEntry[]) => void;
  /** 선택 전체 해제 (집행 완료 후 초기화 등) */
  clear: () => void;
}

export const useRowSelection = (): RowSelection => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((entries: SettlementPendingEntry[]) => {
    const ids = entries.map((e) => e.delivery._id);
    // 최신 상태(prev) 기준으로 전체 선택 여부를 판정해 stale 위험을 없앤다.
    setSelected((prev) => {
      const allSel = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      ids.forEach((id) => (allSel ? next.delete(id) : next.add(id)));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return { selected, toggleRow, toggleGroup, clear };
};
